import { createSlice } from '@reduxjs/toolkit';
import {
  getAmlPfSurvey,
  getAmlPfSurveyResponse,
  getFatca,
  postAml,
  postFatca,
} from './home-onboarding-self-aml-fatca-crs-survey-thunks';
import { AmlFatcaCrsSurveyState } from './home-onboarding-self-aml-fatca-crs-survey-types';

const initialState: AmlFatcaCrsSurveyState = {
  amlTemplate: undefined,
  partialAMLQuestionnaireAnswers: [],
  getAmlStatus: 'idle',
  getAmlError: false,
  getFatcaStatus: 'idle',
  getFatcaError: false,
  postFatcaStatus: 'idle',
  postFatcaError: false,
  datiFatca: undefined,
  postAmlStatus: 'idle',
  postAmlError: false,
  quitAmlFatca: null,
  isAmlFatcaCurrentPage: false,
  amlResponse: undefined,
};

const amlFatcaCrsSurveySlice = createSlice({
  name: 'features/homeOnboardingSelfAmlFatcaCrsSurvey',
  initialState,
  reducers: {
    resetAmlSurvey() {
      return initialState;
    },
    updateAMLPartialAnswers(state, action) {
      const updatedQuestionCodes = new Set(
        action.payload.map((answer) => String(answer.questionCode))
      );

      let answers = state.partialAMLQuestionnaireAnswers.filter(
        (answer) => !updatedQuestionCodes.has(String(answer?.questionCode))
      );
      action.payload.forEach((answer) => {
        answers.push(answer);
      });
      state.partialAMLQuestionnaireAnswers = answers;
    },
    removeAnswerByQuestionId(state, action) {
      const { partialAMLQuestionnaireAnswers } = state;
      state.partialAMLQuestionnaireAnswers =
        partialAMLQuestionnaireAnswers.filter(
          (answer) => String(answer.questionCode) !== String(action.payload)
        );
    },
    setQuitAmlFatca(state, action) {
      state.quitAmlFatca = action.payload;
    },
    setAmlFatcaCurrentPage(state, action) {
      state.isAmlFatcaCurrentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAmlPfSurvey.pending, (state) => {
      state.getAmlStatus = 'loading';
      state.getAmlError = false;
    });
    builder.addCase(getAmlPfSurvey.fulfilled, (state, action) => {
      state.getAmlStatus = 'succeeded';
      state.getAmlError = false;
      state.amlTemplate = action.payload;
      const domande = action.payload.sezioni[0].listaDomande.filter((a) => {
        if (a.idDomanda === 276) return a;
        else return null;
      });
      const risposte =
        domande[0].strutturaInternaDomande.listaDominiRisposte[0].risposte;
      domande[0].strutturaInternaDomande.domande.forEach((e) => {
        risposte.forEach((a) => e.listaRisposte.push(a));
        state.amlTemplate.sezioni[0].listaDomande.push(e);
      });
    });
    builder.addCase(getAmlPfSurvey.rejected, (state) => {
      state.getAmlStatus = 'failed';
      state.getAmlError = true;
    });

    builder.addCase(postAml.pending, (state) => {
      state.postAmlStatus = 'loading';
      state.postAmlError = false;
    });
    builder.addCase(postAml.fulfilled, (state, action) => {
      state.postAmlStatus = 'succeeded';
      state.postAmlError = 'code' in action.payload;
    });
    builder.addCase(postAml.rejected, (state) => {
      state.postAmlStatus = 'failed';
      state.postAmlError = true;
    });

    builder.addCase(getFatca.pending, (state) => {
      state.getFatcaStatus = 'loading';
      state.getFatcaError = false;
    });
    builder.addCase(getFatca.fulfilled, (state, action) => {
      state.getFatcaStatus = 'succeeded';
      state.getFatcaError = 'code' in action.payload;
      state.datiFatca = action.payload;
    });
    builder.addCase(getFatca.rejected, (state) => {
      state.getFatcaStatus = 'failed';
      state.getFatcaError = true;
    });

    builder.addCase(postFatca.pending, (state) => {
      state.postFatcaStatus = 'loading';
      state.postFatcaError = false;
    });
    builder.addCase(postFatca.fulfilled, (state, action) => {
      state.postFatcaStatus = 'succeeded';
      state.postFatcaError = action.payload.code !== '0';
    });
    builder.addCase(postFatca.rejected, (state) => {
      state.postFatcaStatus = 'failed';
      state.postFatcaError = true;
    });
    builder.addCase(getAmlPfSurveyResponse.rejected, (state) => {
      state.getAmlStatus = 'failed';
      state.getAmlError = true;
    });

    builder.addCase(getAmlPfSurveyResponse.pending, (state) => {
      state.getAmlStatus = 'loading';
      state.getAmlError = false;
    });
    builder.addCase(getAmlPfSurveyResponse.fulfilled, (state, action) => {
      state.getAmlStatus = 'succeeded';
      state.getAmlError =
        'code' in action.payload &&
        String((action.payload as any).code) !== '0';
      state.amlResponse = action.payload;
    });
  },
});

export const {
  resetAmlSurvey,
  updateAMLPartialAnswers,
  removeAnswerByQuestionId,
  setQuitAmlFatca,
  setAmlFatcaCurrentPage,
} = amlFatcaCrsSurveySlice.actions;

export default amlFatcaCrsSurveySlice.reducer;
